import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  sameFieldsValidator,
  validCodiceFiscaleValidator,
  validPassphraseValidator,
  validUuidValidator,
} from '@app/components/commons/forms/formly-validators';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { DateLocalizationService } from '@app/services/date-localization.service';
import { ErrorService } from '@app/services/error.service';
import { ModalService } from '@app/services/modal.service';
import { SessionService } from '@app/services/session/session.service';
import { UserSession } from '@app/store/models';
import { RenewalData } from '@app/store/models/certificate.model';
import { GlobalState } from '@app/store/store';
import { Button } from '@infocert/ng-ui-kit/models/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import { finalize, lastValueFrom, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-renew',
  templateUrl: './renew.component.html',
  styleUrls: ['./renew.component.scss'],
  standalone: false,
})
export class RenewPageComponent implements OnInit {
  constructor(
    private store: Store<GlobalState>,
    private sessionService: SessionService,
    private certificateService: CertificateService,
    private otpService: OtpService,
    private errorService: ErrorService,
    private modalService: ModalService,
    private bsModalService: BsModalService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private dateLocalizationService: DateLocalizationService
  ) {}

  @ViewChild('ConditionsCheckbox', { read: ElementRef })
  checkboxConditionsEl!: ElementRef;
  @ViewChild('ConfirmCheckbox', { read: ElementRef })
  checkboxConfirmEl!: ElementRef;
  @ViewChild('UuidReplacementNoticeCheckbox', {
    read: ElementRef,
  })
  checkboxUuidReplacementNoticeEl!: ElementRef;

  session: UserSession | undefined;
  loading = false;

  showContractSignStep: boolean = false;
  showContractSignOTP: boolean = false;
  showAuthorizationStep: boolean = false;
  showEmissionStep: boolean = false;
  showEmissionOTP: boolean = false;
  contractSignOtpAttempted: boolean = false;
  contractSignPinAttempted: boolean = false;
  contractSignGeneralConsentAttempted: boolean = false;
  contractSignPersonalConsentAttempted: boolean = false;
  contractSignUuidConsentAttempted: boolean = false;
  emissionOtpAttempted: boolean = false;
  raoAttempted: boolean = false;
  couponAttempted: boolean = false;
  passphraseAttempted: boolean = false;
  emissionPinAttempted: boolean = false;
  showPassphraseStep: boolean = false;
  showError: boolean = false;
  showSpinner: boolean = false;

  contractSignStepNumber: number;
  authorizationStepNumber: number;
  passphraseStepNumber: number;
  emissionStepNumber: number;

  renewalData: RenewalData;
  // RAO-only shortcut currently disabled: keep flag false until flow is reintroduced.
  onlyRAOpayment: boolean = false;
  paymentMode: string = '';
  needsPayment: boolean = true;
  redirectingToShop: boolean = false;

  expiringDate: string = '';

  automatic: boolean = false;
  isPkBox: boolean = false;

  certType: string = '';
  active: boolean = true;
  nome: string = '';
  cognome: string = '';
  iut: string = '';
  expiringDateTextFormat: string = '';
  serialNumber: string;
  CF: string = '';
  statements: string[];
  todayDate: string;
  noPayment: boolean = false;
  isPkBoxOnPkBox = false;
  IsTriss: boolean = false;
  telephone: string = '';

  get shopCardButton(): Button {
    return {
      label: this.translateService.instant('RENEW_PAGE.BUTTON.SIGN'),
      color: 'text',
      disabled: !this.active,
      onClick: () => this.openModal('SHOP'),
    };
  }

  get raoCardButton(): Button {
    return {
      label: this.translateService.instant('RENEW_PAGE.BUTTON.RAO'),
      color: 'text',
      disabled: !this.active,
      onClick: () => this.openModal('RAO'),
    };
  }

  get couponCardButton(): Button {
    return {
      label: this.translateService.instant('RENEW_PAGE.BUTTON.COUPON'),
      color: 'text',
      disabled: !this.active,
      onClick: () => this.openModal('COUPON'),
    };
  }

  openOnce: boolean = false;
  isStellantis: boolean = false;
  isMigration: boolean = false;
  isNewFlow: boolean = false;
  isForcedLogout: boolean = false; // logout forzato dopo il rinnovo

  // FormControl per il codice fiscale, con validazione personalizzata della struttura formale
  codiceFiscaleControl = new FormControl('', [validCodiceFiscaleValidator]);
  // FormControl per l'UUID, con validazione personalizzata basata sul formato UUID (v1–v5)
  uuidControl = new FormControl('', [validUuidValidator]);

  ngOnInit(): void {
    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.session = store.session;
            this.automatic = store.session.automatic;
            this.IsTriss = this.session.trissUser;
            if (this.session.automatic) {
              this.onlyRAOpayment = true;
            }


            if (!this.session.icssUser && !this.session.trissUser) {
              this.isPkBox = true;
            }
          }
          if (store.certificate) {
            this.active = store.certificate.active;
            this.certType = store.certificate.certificateType;
            if (store.certificate.certificateInfo.subjectDN != null) {
              //subjectDN
              this.nome = store.certificate.certificateInfo.subjectDN.CN;
              this.iut =
                store.certificate.certificateInfo.subjectDN.dnQualifier;
              this.serialNumber =
                store.certificate.certificateInfo.subjectDN.SERIALNUMBER;
            } else {
              //subjectDnMap
              this.nome =
                store.certificate.certificateInfo.subjectDnMap.commonName;
              this.iut =
                store.certificate.certificateInfo.subjectDnMap.dnQualifier;
              this.serialNumber =
                store.certificate.certificateInfo.subjectDnMap.serialNumber;
            }
            if (store.certificate.telephone) {
              this.telephone = store.certificate.telephone;
            }

            const validityDate = moment(
              store.certificate.validityTo.substring(0, 10)
            )
              .utc()
              .local();

            this.expiringDate =
              this.dateLocalizationService.formatDisplayDate(validityDate);
            this.expiringDateTextFormat =
              this.dateLocalizationService.formatCompactDate(validityDate);
            if (this.serialNumber.includes('TINIT-')) {
              this.CF = this.serialNumber.slice(6);
            }
            this.telephone = store.certificate.telephone || '';
            const todayDate = new Date();
            //formatto la data e riempio il campo
            this.todayDate = `${todayDate
              .getDate()
              .toString()
              .padStart(2, '0')}/${(todayDate.getMonth() + 1)
              .toString()
              .padStart(2, '0')}/${todayDate.getFullYear()}`;

            this.codiceFiscaleControl.setValue(this.serialNumber.slice(6));
            this.uuidControl.setValue(this.serialNumber.slice(6));
            console.log('CF:', this.serialNumber.slice(6));
            this.buildStatements();
            console.log('statements:', this.statements);
          }
        })
      )
      .subscribe();

    this.checkRenewalState();

    this.isPkBoxOnPkBox =
      this.isPkBox && this.session.migrationStatus == 'NOT_AVAILABLE';

    this.updateFlowFlags();
    this.setupValidationAttemptReset();
  }

  private setupValidationAttemptReset(): void {
    this.username.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.raoAttempted = false));
    this.password.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.raoAttempted = false));
    this.coupon.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.couponAttempted = false));
    this.passphrase.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.passphraseAttempted = false));
    this.newPin.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.emissionPinAttempted = false));
    this.confirmPin.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.emissionPinAttempted = false));
    this.fContractSign['pin'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.contractSignPinAttempted = false));
    this.fContractSign['otp'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.contractSignOtpAttempted = false));
    this.fEmission['otp'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.emissionOtpAttempted = false));
  }

  private buildStatements() {
    this.statements = [];
    if (this.session.trissUser) {
      this.statements.push(
        this.translateService.instant(
          'RENEW_PAGE.CONTRACT_SIGN_STEP.DESC_PERSONAL_INFORMATION_TRISS',
          {
            nome: this.nome,
            taxCode: this.CF,
            iut: this.iut,
            date: this.expiringDate,
            renewalDate: this.todayDate,
          }
        )
      );
    } else {
      this.statements.push(
        this.translateService.instant(
          'RENEW_PAGE.CONTRACT_SIGN_STEP.DESC_PERSONAL_INFORMATION',
          {
            nome: this.nome,
            iut: this.iut,
            date: this.expiringDate,
          }
        )
      );
    }
    this.statements.push(
      this.translateService.instant(
        'RENEW_PAGE.CONTRACT_SIGN_STEP.DESC_CONFIRM'
      )
    );
    this.statements.push(
      this.translateService.instant(
        'RENEW_PAGE.CONTRACT_SIGN_STEP.DESC_PERSONAL_INFORMATION_CONFIRMATION'
      )
    );
    if (this.codiceFiscaleControl.errors && this.uuidControl.errors) {
      this.statements.push(
        this.translateService.instant(
          'RENEW_PAGE.CONTRACT_SIGN_STEP.UUID_REPLACEMENT_NOTICE'
        )
      );
    }
  }

  async checkRenewalState() {
    this.certificateService.getPromiseCertificate();

    if (!this.active) {
      //apro la modale d'errore con l'errore segnalato
      this.errorService.openErrorModal(
        this.translateService.instant('RENEW_PAGE.DEACTIVE_FA_ERROR')
      );
    }

    this.renewalData = await lastValueFrom(
      this.certificateService.getRenewalState()
    );

    this.resetAccordionVisibility();

    this.needsPayment = this.renewalData.payment;
    this.isStellantis = this.renewalData.stellantis;
    if (!this.automatic) {
      this.isMigration = !!this.renewalData.migration;
    }
    this.updateFlowFlags();
    //noPayment è la variabile per controllare se è stato fatto il pagamento per i rinnovi triss
    this.noPayment = this.renewalData.noPayment;
    console.log('noPayment', this.noPayment);

    //status è la variabile per lo stato dei rinnovi triss
    switch (this.renewalData.state || this.renewalData.status) {
      case 'NOT_RENEWABLE':
        // certificato non rinnovabile
        this.showError = true;
        break;
      case 'RENEWABLE':
        this.showContractSignStep = true;
        break;
      case 'CONTRACT_SIGNED':
        if (this.isNewFlow) {
          //se triss apro lo step della passphrase
          if (this.session.trissUser || (!this.automatic && this.isMigration)) {
            this.goToPassphraseStep();
          } else {
            this.showEmissionStep = true;
          }
          if (!this.openOnce) {
            this.openModal('SHOP');
            this.openOnce = true;
          }
        } else {
          this.showAuthorizationStep = true;
        }
        break;
      case 'WAITING_PAYMENT_CONFIRMATION':
        this.showAuthorizationStep = true;
        break;
      case 'READY_TO_EMIT':
        //se triss apro lo step della passphrase
        if (this.session.trissUser) {
          this.goToPassphraseStep();
        } else {
          this.showEmissionStep = true;
        }
        if (!this.openOnce) {
          this.openModal('SHOP');
          this.openOnce = true;
        }

        break;
      case 'RENEWED':
        // certificato già rinnovato
        this.showError = true;
        break;
      default:
        //stato non valido, mostrare errore
        this.showError = true;
        break;
    }

    if (
      this.renewalData.state === 'READY_TO_EMIT' &&
      this.followMigrationFlow
    ) {
      this.showEmissionStep = false;
      this.showPassphraseStep = true;
    }

    if (this.session?.automatic) {
      this.contractSignStepNumber = 1;
      // Rinnovo automatico: migration non aggiunge step.
      // Con pagamento: 1 Consensi, 2 Autenticazione, 3 Negozio, 4 Emissione.
      // Senza pagamento: 1 Consensi, 2 Autenticazione, 3 Emissione.
      if (this.renewalData.payment || this.renewalData.noPayment === false) {
        this.authorizationStepNumber = 3;
        this.passphraseStepNumber = 0;
        this.emissionStepNumber = 4;
      } else {
        this.passphraseStepNumber = 0;
        this.emissionStepNumber = 3;
      }
    } else {
      this.contractSignStepNumber = 1;
      // contract sign otp step = 2
      if (this.renewalData.payment || this.renewalData.noPayment === false) {
        if (this.isNewFlow) {
          this.authorizationStepNumber = 5;
        } else {
          this.authorizationStepNumber = 3;
        }

        // se triss o migration true c'è anche lo step passphrase
        if (this.renewalData.migration || this.session.trissUser) {
          if (this.isNewFlow) {
            this.passphraseStepNumber = 3;
            this.emissionStepNumber = 4;
            this.authorizationStepNumber = 5;
          } else {
            this.passphraseStepNumber = 4;
            this.emissionStepNumber = 5;
          }
        } else {
          if (this.isNewFlow) {
            this.emissionStepNumber = 3;
            this.authorizationStepNumber = 4;
          } else {
            this.emissionStepNumber = 4;
          }
        }
      } else {
        // se triss o migration true c'è anche lo step passphrase
        if (this.renewalData.migration || this.session.trissUser) {
          if (this.isNewFlow) {
            this.passphraseStepNumber = 3;
            this.emissionStepNumber = 4;
            this.authorizationStepNumber = 5;
          } else {
            this.passphraseStepNumber = 3;
            this.emissionStepNumber = 4;
          }
        } else {
          this.emissionStepNumber = 3;
        }
      }
    }

  }

  private resetAccordionVisibility(): void {
    this.showContractSignStep = false;
    this.showContractSignOTP = false;
    this.showAuthorizationStep = false;
    this.showPassphraseStep = false;
    this.showEmissionStep = false;
    this.showEmissionOTP = false;
    this.showError = false;
  }

  // contract sign form

  isGeneralConditionsChecked: boolean = false;
  isPersonalDataChecked: boolean = false;
  isUuidNoticeChecked: boolean = false;
  userConfirmedPersonalData = new UntypedFormControl('', [Validators.required]);

  pin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  otp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  contractSignForm = new UntypedFormGroup({
    userConfirmedPersonalData: this.userConfirmedPersonalData,
    pin: this.pin,
    otp: this.otp,
  });

  // convenience getter for easy access to form fields
  get fContractSign() {
    return this.contractSignForm.controls;
  }

  get contractSignFormErrors() {
    const errors = this.contractSignForm.errors;
    return errors;
  }

  get shouldSkipContractSignOtp(): boolean {
    return !!(this.session?.automatic && this.session?.icssUser !== true);
  }

  // Verifica formato OTP: solo cifre, lunghezza 6-8 (coerente con i vincoli attuali).
  // Serve per mostrare l'errore usando direttamente il componente `ng-ui-kit-otp`.
  isOtpFormatValid(otp: unknown): boolean {
    const raw = (otp ?? '').toString().trim();
    if (!raw || raw === 'null') return false;
    return /^\d{6,8}$/.test(raw);
  }

  private getOtpDisclaimerText(otpControl: UntypedFormControl): string {
    // Se il campo è vuoto (o 'null' usato nei flow), mostriamo sempre "campo obbligatorio"
    // indipendentemente dagli errori presenti nel control (per Renew non c'è Validators.required).
    const rawValue = (otpControl?.value ?? '').toString().trim();
    if (!rawValue || rawValue === 'null') {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }

    const errors = otpControl?.errors;
    if (!errors) return '';

    // Replica i messaggi di `app-field-validator` per minlength/maxlength.
    if (errors['minlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MIN_LENGTH');
    }
    if (errors['maxlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MAX_LENGTH');
    }
    if (errors['required']) {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }
    return '';
  }

  getContractSignOtpErrorDisclaimerText(): string {
    if (!this.contractSignOtpAttempted) return '';
    const otpControl = this.fContractSign['otp'] as UntypedFormControl;
    if (this.isOtpFormatValid(otpControl.value)) return '';
    return this.getOtpDisclaimerText(otpControl);
  }

  getEmissionOtpErrorDisclaimerText(): string {
    const otpControl = this.fEmission['otp'] as UntypedFormControl;
    if (!this.emissionOtpAttempted || this.isNewFlow) return '';
    if (this.isOtpFormatValid(otpControl.value)) return '';
    return this.getOtpDisclaimerText(otpControl);
  }

  private getPinDisclaimerText(pinControl: UntypedFormControl): string {
    const errors = pinControl?.errors;
    if (!errors) return '';

    // Replica i messaggi di `app-field-validator` per required/minlength/maxlength.
    if (errors['required']) {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }
    if (errors['minlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MIN_LENGTH');
    }
    if (errors['maxlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MAX_LENGTH');
    }
    return '';
  }

  getContractSignPinErrorDisclaimerText(): string {
    if (!this.contractSignPinAttempted) return '';
    const pinControl = this.fContractSign['pin'] as UntypedFormControl;
    if (pinControl.valid) return '';
    return this.getPinDisclaimerText(pinControl);
  }

  goToContractSignStep() {
    this.showContractSignStep = true;
    this.showContractSignOTP = false;
    this.contractSignOtpAttempted = false;
    this.contractSignPinAttempted = false;
    this.otpService.resetOtpInputGroup('sms');
    const otpControl = this.fContractSign['otp'] as UntypedFormControl;
    otpControl.setValidators([
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    otpControl.updateValueAndValidity({ emitEvent: false });
    otpControl.reset('', { emitEvent: false });
  }

  goToContractSignOTP() {
    if (this.userConfirmedPersonalData.invalid) {
      this.contractSignGeneralConsentAttempted = !this.isGeneralConditionsChecked;
      this.contractSignPersonalConsentAttempted = !this.isPersonalDataChecked;
      this.contractSignUuidConsentAttempted =
        this.codiceFiscaleControl.errors &&
        this.uuidControl.errors &&
        !this.isPkBoxOnPkBox
          ? !this.isUuidNoticeChecked
          : false;
      this.userConfirmedPersonalData.markAsTouched();
      return;
    }
    this.contractSignGeneralConsentAttempted = false;
    this.contractSignPersonalConsentAttempted = false;
    this.contractSignUuidConsentAttempted = false;
    // SE FA NON SERVE L'OTP PER IL CONTRACT SIGN
    const skipOtp = this.shouldSkipContractSignOtp;
    const otpControl = this.fContractSign['otp'] as UntypedFormControl;
    this.showContractSignStep = false;
    this.showContractSignOTP = true;
    this.contractSignOtpAttempted = false;
    this.contractSignPinAttempted = false;
    this.otpService.resetOtpInputGroup('sms'); // pulisci la UI lo stesso

    if (skipOtp) {
      otpControl.clearValidators();
      otpControl.updateValueAndValidity({ emitEvent: false });
      otpControl.setValue('null');
    } else {
      otpControl.setValidators([
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);
      otpControl.updateValueAndValidity({ emitEvent: false });
      otpControl.reset('', { emitEvent: false });
      this.sendOtp();
    }
  }

  onContractSignSubmit() {
    const otpControl = this.fContractSign['otp'] as UntypedFormControl;
    const skipOtp = this.shouldSkipContractSignOtp;
    if (skipOtp) {
      otpControl.clearValidators();
      otpControl.updateValueAndValidity({ emitEvent: false });
      otpControl.setValue('null');
    } else {
      otpControl.setValidators([
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);
      otpControl.updateValueAndValidity({ emitEvent: false });
    }
    const pinControl = this.fContractSign['pin'] as UntypedFormControl;
    if (pinControl.invalid) {
      this.contractSignPinAttempted = true;
      pinControl.markAsTouched();
    }
    // Se siamo nello step OTP e il formato non è idoneo, mostriamo l'errore nel componente OTP
    // e restiamo sullo stesso step (non blocchiamo l'accordion chiudendola).
    if (!skipOtp && !this.isOtpFormatValid(otpControl.value)) {
      this.contractSignOtpAttempted = true;
      otpControl.markAsTouched();
      this.stopBusy();
      return;
    }
    if (this.contractSignForm.invalid) {
      this.stopBusy();
      return;
    }
    this.loading = true;

    this.certificateService
      //@ts-ignore
      .contractSign(
        this.fContractSign['pin'].value.trim(),
        this.fContractSign['userConfirmedPersonalData'].value,
        this.fContractSign['otp'].value.trim(),
        this.statements
      )
      .pipe(finalize(() => this.stopBusy()))
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene
          this.showContractSignOTP = false;
          this.showContractSignStep = false;
          //se triss apro lo steo della password

          const hasPayment =
            this.renewalData.payment || this.renewalData.noPayment === false;

          if (this.isNewFlow) {
            // NEW FLOW: vai a Passphrase o Emission, NON aprire Authorization ora
            if (this.followMigrationFlow || this.session.trissUser) {
              this.showPassphraseStep = true;
            } else {
              this.showEmissionStep = true;
            }
          } else {
            // OLD FLOW: ok aprire Authorization subito se serve
            if (hasPayment) {
              this.showAuthorizationStep = true;
            } else if (this.followMigrationFlow || this.session.trissUser) {
              this.showPassphraseStep = true;
            } else {
              this.showEmissionStep = true;
            }
          }
        },
        error: error => {
          console.error(error);
          this.resetContractPinAndOtp();
          this.otpService.resetOtpInputGroup('sms');
          this.goToContractSignStep();
          this.clearContractSignForm();
          //chiude la modale per levare le spunte nel checkbox

          this.resetCheckboxes();
          this.loading = false;
          this.showSpinner = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  clearContractSignForm() {
    this.contractSignGeneralConsentAttempted = false;
    this.contractSignPersonalConsentAttempted = false;
    this.contractSignUuidConsentAttempted = false;
    this.pin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.isGeneralConditionsChecked = false;
    this.isPersonalDataChecked = false;
    this.isUuidNoticeChecked = false;
    this.userConfirmedPersonalData = new UntypedFormControl('', [
      Validators.required,
    ]);

    this.otp = new UntypedFormControl('', [
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.contractSignForm = new UntypedFormGroup({
      userConfirmedPersonalData: this.userConfirmedPersonalData,
      pin: this.pin,
      otp: this.otp,
    });

    this.otpService.resetOtpInputGroup('sms');
  }

  private resetContractPinAndOtp() {
    this.fContractSign?.['pin']?.reset('');
    this.fContractSign?.['otp']?.reset('');
    this.fContractSign?.['pin']?.markAsPristine();
    this.fContractSign?.['otp']?.markAsPristine();
    this.fContractSign?.['pin']?.markAsUntouched();
    this.fContractSign?.['otp']?.markAsUntouched();
    this.contractSignForm?.markAsPristine();
    this.contractSignForm?.markAsUntouched();
    this.contractSignForm?.setErrors(null);
    this.contractSignForm?.updateValueAndValidity({ emitEvent: false });
    this.otpService.resetOtpInputGroup('contract-pin');
  }

  checkDataConfirmation(event: any, id: string) {
    if (event.target.checked) {
      switch (id) {
        case 'general':
          this.isGeneralConditionsChecked = true;
          this.contractSignGeneralConsentAttempted = false;
          break;
        case 'personal':
          this.isPersonalDataChecked = true;
          this.contractSignPersonalConsentAttempted = false;
          break;
        case 'uuidNotice':
          this.isUuidNoticeChecked = true;
          this.contractSignUuidConsentAttempted = false;
          break;
      }
    } else {
      switch (id) {
        case 'general':
          this.isGeneralConditionsChecked = false;
          this.contractSignGeneralConsentAttempted = false;
          break;
        case 'personal':
          this.isPersonalDataChecked = false;
          this.contractSignPersonalConsentAttempted = false;
          break;
        case 'uuidNotice':
          this.isUuidNoticeChecked = false;
          this.contractSignUuidConsentAttempted = false;
          break;
      }
    }
    if (this.codiceFiscaleControl.errors && this.uuidControl.errors) {
      if (this.isPkBoxOnPkBox) {
        if (
          this.isGeneralConditionsChecked === true &&
          this.isPersonalDataChecked === true
        ) {
          this.fContractSign['userConfirmedPersonalData'].setValue(true);
        } else {
          this.fContractSign['userConfirmedPersonalData'].setValue('');
        }
      } else {
        if (
          this.isGeneralConditionsChecked === true &&
          this.isPersonalDataChecked === true &&
          this.isUuidNoticeChecked === true
        ) {
          this.fContractSign['userConfirmedPersonalData'].setValue(true);
        } else {
          this.fContractSign['userConfirmedPersonalData'].setValue('');
        }
      }
    } else {
      if (
        this.isGeneralConditionsChecked === true &&
        this.isPersonalDataChecked === true
      ) {
        this.fContractSign['userConfirmedPersonalData'].setValue(true);
      } else {
        this.fContractSign['userConfirmedPersonalData'].setValue('');
      }
    }
  }

  // end contract sign form

  // rao form

  username = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(5),
  ]);

  password = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
  ]);

  raoForm = new UntypedFormGroup({
    username: this.username,
    password: this.password,
  });

  // convenience getter for easy access to form fields
  get fRAO() {
    return this.raoForm.controls;
  }

  get raoFormErrors() {
    const errors = this.raoForm.errors;
    return errors;
  }

  onRAOsubmit() {
    if (this.raoForm.invalid) {
      this.raoAttempted = true;
      this.fRAO['username']?.markAsTouched();
      this.fRAO['password']?.markAsTouched();
      this.stopBusy();
      return;
    }
    this.raoAttempted = false;
    this.loading = true;
    if (this.isNewFlow && this.isForcedLogout) {
      this.showSpinner = true;
    }

    // stop here if form is invalid
    if (this.raoForm.invalid) {
      return;
    }

    this.certificateService
      //@ts-ignore
      .renewRAO(
        this.fRAO['password'].value.trim(),
        this.fRAO['username'].value
          .replace(/[^\w\s]/g, '')
          .toUpperCase()
          .trim()
      )
      .pipe(finalize(() => this.stopBusy()))
      .subscribe({
        next: async response => {
          this.loading = false;
          if (this.isNewFlow) {
            if (this.isForcedLogout) {
              this.showSpinner = false;
              await this.logoutAndCloseAllModals();
            } else {
              this.goToSuccessPage();
            }
          }
          //   this.showSpinner = false;
          //   this.localStorageService.store('clearSession', true);
          //   window.location.reload();
          //   this.modalService.closeModal();
          //   this.refreshSessionData();

          // Se tutto va bene
          this.showAuthorizationStep = false;
          if (!this.isNewFlow) {
            if (this.followMigrationFlow) {
              this.showPassphraseStep = true;
            } else {
              this.showEmissionStep = true;
            }
          }
        },
        error: error => {
          console.error(error);
          this.clearRAOForm();

          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });

    this.loading = false;
  }

  clearRAOForm() {
    this.raoAttempted = false;
    this.username = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(5),
    ]);
    this.password = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
    ]);

    this.raoForm = new UntypedFormGroup({
      username: this.username,
      password: this.password,
    });
  }

  // end rao form

  // coupon form

  coupon = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(3),
  ]);

  couponForm = new UntypedFormGroup({
    coupon: this.coupon,
  });

  // convenience getter for easy access to form fields
  get fCoupon() {
    return this.couponForm.controls;
  }

  get couponFormErrors() {
    const errors = this.couponForm.errors;
    return errors;
  }

  onCouponSubmit() {
    if (this.couponForm.invalid) {
      this.couponAttempted = true;
      this.fCoupon['coupon']?.markAsTouched();
      this.stopBusy();
      return;
    }
    this.couponAttempted = false;
    this.loading = true;
    if (this.isNewFlow && this.isForcedLogout) {
      this.showSpinner = true;
    }
    this.certificateService
      //@ts-ignore
      .renewCoupon(this.fCoupon['coupon'].value.trim())
      .pipe(finalize(() => this.stopBusy()))
      .subscribe({
        next: async response => {
          this.loading = false;

          if (this.isNewFlow) {
            if (this.isForcedLogout) {
              this.showSpinner = false;
              await this.logoutAndCloseAllModals();
            } else {
              this.goToSuccessPage();
            }
          }

          // Se tutto va bene
          this.showAuthorizationStep = false;

          if (this.followMigrationFlow) {
            this.showPassphraseStep = true;
          } else {
            this.showEmissionStep = true;
          }
        },
        error: error => {
          console.error(error);
          this.clearCouponForm();

          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  clearCouponForm() {
    this.couponAttempted = false;
    this.coupon = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]);

    this.couponForm = new UntypedFormGroup({
      coupon: this.coupon,
    });
  }

  // end coupon form

  // shop submit

  onShopSubmit() {
    this.loading = true;

    this.certificateService
      //@ts-ignore
      .renewShop()
      .pipe(finalize(() => this.stopBusy()))
      .subscribe({
        next: async response => {
          this.redirectingToShop = true;
          if (response.shopUrl) {
            setTimeout(() => {
              this.loading = false;
              window.location.href = response.shopUrl;
            }, 4000);
          }
        },
        error: error => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  // end shop submit

  // passphrase form

  passphrase = new UntypedFormControl('', [
    Validators.required,
    validPassphraseValidator,
  ]);

  passphraseForm = new UntypedFormGroup({
    passphrase: this.passphrase,
  });

  // convenience getter for easy access to form fields
  get fPassphrase() {
    return this.passphraseForm.controls;
  }

  get passphraseFormErrors() {
    const errors = this.passphraseForm.errors;
    return errors;
  }

  onPassphraseSubmit() {
    if (this.passphraseForm.invalid) {
      this.passphraseAttempted = true;
      this.fPassphrase['passphrase']?.markAsTouched();
      this.stopBusy();
      return;
    }
    this.passphraseAttempted = false;
    this.loading = true;
    this.goToEmissionStep();
    this.stopBusy();
    this.otpService.resetSmsOtpInputs();
  }

  clearPassphraseForm() {
    this.passphraseAttempted = false;
    this.passphrase = new UntypedFormControl('', [
      Validators.required,
      validPassphraseValidator,
    ]);

    this.passphraseForm = new UntypedFormGroup({
      passphrase: this.passphrase,
    });

    // Il control viene ricreato: ri-aggancia il reset dell'errore mentre l'utente digita.
    this.passphrase.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.passphraseAttempted = false));
  }

  setCF(event: any) {
    if (event.target.checked) {
      // Se la checkbox è selezionata, imposta CF come passphrase
      this.fPassphrase['passphrase'].setValue(this.CF);
    } else {
      // Se la checkbox viene deselezionata, svuota la passphrase
      this.fPassphrase['passphrase'].setValue('');
    }
    // Aggiorna la validazione anche in questa opzione
    this.fPassphrase['passphrase'].updateValueAndValidity();
  }

  // end passphrase form

  // emission form

  newPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  confirmPin = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(8),
  ]);

  emissionOtp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  emissionForm = new UntypedFormGroup(
    {
      newPin: this.newPin,
      confirmPin: this.confirmPin,
      otp: this.emissionOtp,
    },
    {
      validators: sameFieldsValidator('newPin', 'confirmPin'),
    }
  );

  // convenience getter for easy access to form fields
  get fEmission() {
    return this.emissionForm.controls;
  }

  get emissionFormErrors() {
    const errors = this.emissionForm.errors;
    return errors;
  }

  hasEmissionPinsAtMinLength(minLength: number = 8): boolean {
    const newPinValue = (this.fEmission['newPin']?.value ?? '').toString();
    const confirmPinValue = (this.fEmission['confirmPin']?.value ?? '').toString();
    return newPinValue.length >= minLength && confirmPinValue.length >= minLength;
  }

  goToEmissionStep() {
    this.showPassphraseStep = false;
    this.showEmissionStep = true;
    this.showEmissionOTP = false;
    this.emissionOtpAttempted = false;
    this.emissionPinAttempted = false;
    if (!this.isNewFlow) {
      this.otpService.resetOtpInputGroup('sms');
      this.fEmission['otp']?.setValue?.(''); // opzionale
    }
  }

  goToPassphraseStep() {
    this.showPassphraseStep = true;
    this.showEmissionStep = false;
    this.showEmissionOTP = false;
    this.otpService.resetOtpInputGroup('sms');
  }

  goToEmissionOTP() {
    // New Flow: niente OTP
    if (this.isNewFlow) {
      this.onEmissionSubmit();
      return;
    }
    this.otpService.resetOtpInputGroup('sms'); // pulisci la UI lo stesso
    // Niente OTP per la migration (non automatica) e per la FA PKBox.
    if (this.followMigrationFlow || (this.automatic && this.isPkBox)) {
      this.onEmissionSubmit();
    } else {
      // Blocca lo step OTP se PIN / conferma PIN non sono validi:
      // così l'utente resta nello stesso accordion e vede gli errori in tempo reale
      // (anche al click del bottone "Activate Cert").
      if (this.emissionForm.invalid) {
        this.emissionPinAttempted = true;
        this.fEmission['newPin']?.markAsTouched();
        this.fEmission['confirmPin']?.markAsTouched();
        return;
      }

      this.showEmissionStep = false;
      this.showEmissionOTP = true;
      this.emissionOtpAttempted = false;
      this.sendOtp();
    }
  }

  onEmissionSubmit() {
    // Sul Renew, nello step OTP dell'emissione, il bottone resta abilitato:
    // se l'OTP non rispetta il formato, mostriamo l'errore direttamente
    // nel componente `ng-ui-kit-otp` e rimaniamo bloccati sullo stesso step.
    if (!this.isNewFlow && this.showEmissionOTP) {
      const otpControl = this.fEmission['otp'] as UntypedFormControl;
      if (!this.isOtpFormatValid(otpControl.value)) {
        this.emissionOtpAttempted = true;
        otpControl.markAsTouched();
        this.stopBusy();
        return;
      }
    }
    if (this.emissionForm.invalid) {
      this.emissionPinAttempted = true;
      this.fEmission['newPin']?.markAsTouched();
      this.fEmission['confirmPin']?.markAsTouched();
      this.stopBusy();
      return;
    }
    this.emissionPinAttempted = false;
    this.loading = true;

    if (this.isNewFlow) {
      this.fEmission['otp'].setValue('null');
    }

    if (this.followMigrationFlow) {
      this.fEmission['otp'].setValue('null');
      if (!this.isNewFlow) {
        this.showSpinner = true;
      }

      this.certificateService
        //@ts-ignore
        .renewCert(
          this.fEmission['confirmPin'].value.trim(),
          this.fEmission['otp'].value.trim(),
          this.fPassphrase['passphrase'].value.trim()
        )
        .pipe(finalize(() => this.stopBusy()))
        .subscribe({
          next: async response => {
            this.loading = false;

            // Se tutto va bene
            this.showSpinner = false;
            this.showEmissionOTP = false;
            this.showEmissionStep = false;
            this.openAuthorizationIfNeeded();
            /* this.toasterService.success(
              this.translateService.instant(
                'RENEW_PAGE.SUCCESS_STEP.FORCE_LOGOUT.SUCCESS_MESSAGE'
              ),
              '',
              {
                positionClass: 'toast-top-right',
                timeOut: 3000,
              }
            ); */

            // force logout in caso di successo
            if (!this.isNewFlow) {
              this.localStorageService.store('clearSession', true);
              this.closeModal();
              window.location.reload();
            }
          },
          error: error => {
            if (!this.isNewFlow) {
              this.otpService.resetOtpInputGroup('sms');
              this.fEmission['otp']?.setValue?.(''); // opzionale
            }
            console.error(error);
            this.resetEmissionPins();
            this.showSpinner = false;
            this.showEmissionStep = false;
            this.showPassphraseStep = true;

            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
    } else {
    if (this.automatic) {
      // se PkBox FA passare otp null
      if (this.isPkBox) {
        this.fEmission['otp'].setValue('null');
      }
      // Rinnovo FA: come migration, niente /renew/success. Reload e poi attivazione.
      if (!this.isNewFlow) {
        this.showSpinner = true;
      }
      this.certificateService
        //@ts-ignore
        .renewCert(
          this.fEmission['confirmPin'].value,
          this.fEmission['otp'].value
        )
        .pipe(finalize(() => this.stopBusy()))
        .subscribe({
          next: async response => {
            this.loading = false;
            this.showSpinner = false;
            this.showEmissionOTP = false;
            this.showEmissionStep = false;
            this.openAuthorizationIfNeeded();
            if (!this.isNewFlow) {
              this.localStorageService.store('clearSession', true);
              this.closeModal();
              window.location.reload();
            }
          },
          error: error => {
            if (!this.isNewFlow) {
              this.otpService.resetOtpInputGroup('sms');
              this.fEmission['otp']?.setValue?.(''); // opzionale
            }
            this.resetEmissionPins();
            this.goToEmissionStep();
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
      } else {
        //se è triss mando anche la passphrase
        if (this.session.trissUser) {
          //inibisco la visibiltà del toast d'errore per evitare che appaia visibile l'errore di autenticazione nel toast prima del refresh della session
          localStorage.setItem('error_toast', 'not_visible');
          // RINNOVO !MIGRATION E FR
          this.certificateService
            //@ts-ignore
            .renewCert(
              this.fEmission['confirmPin'].value,
              this.fEmission['otp'].value,
              this.fPassphrase['passphrase'].value
            )
            .pipe(finalize(() => this.stopBusy()))
            .subscribe({
              next: async response => {
                this.loading = false;

                // Se tutto va bene
                this.refreshSessionData();
                this.showEmissionOTP = false;
                this.showEmissionStep = false;
                this.openAuthorizationIfNeeded();
                if (!this.isNewFlow) {
                  this.goToSuccessPage();
                  this.closeModal();
                }
                setTimeout(() => {
                  localStorage.removeItem('error_toast');
                }, 3000); // 3000 millisecondi = 3 secondi
              },
              error: error => {
                console.error(error);
                if (!this.isNewFlow) {
                  this.otpService.resetOtpInputGroup('sms');
                  this.fEmission['otp']?.setValue?.(''); // opzionale
                }
                this.refreshSessionData();
                this.clearPassphraseForm();
                this.clearEmissionForm();
                this.goToPassphraseStep();
                setTimeout(() => {
                  localStorage.removeItem('error_toast');
                }, 3000); // 3000 millisecondi = 3 secondi

                this.loading = false;
              },
              complete: () => {
                this.loading = false;
              },
            });
        } else {
          // RINNOVO !MIGRATION E FR
          this.certificateService
            //@ts-ignore
            .renewCert(
              this.fEmission['confirmPin'].value,
              this.fEmission['otp'].value
            )
            .pipe(finalize(() => this.stopBusy()))
            .subscribe({
              next: async response => {
                this.loading = false;

                // Se tutto va bene
                this.showEmissionOTP = false;
                this.showEmissionStep = false;
                this.openAuthorizationIfNeeded();
                if (!this.isNewFlow) {
                  this.goToSuccessPage();
                  this.closeModal();
                }
              },
              error: error => {
                console.error(error);
                if (!this.isNewFlow) {
                  this.otpService.resetOtpInputGroup('sms');
                  this.fEmission['otp']?.setValue?.(''); // opzionale
                }
                this.resetEmissionPins();
                this.goToEmissionStep();

                this.loading = false;
              },
              complete: () => {
                this.loading = false;
              },
            });
        }
      }
    }
  }

  clearEmissionForm() {
    this.emissionPinAttempted = false;
    this.emissionOtpAttempted = false;
    this.newPin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.confirmPin = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(8),
    ]);

    this.emissionOtp = new UntypedFormControl('', [
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.emissionForm = new UntypedFormGroup(
      {
        newPin: this.newPin,
        confirmPin: this.confirmPin,
        otp: this.emissionOtp,
      },
      {
        validators: sameFieldsValidator('newPin', 'confirmPin'),
      }
    );

    this.otpService.resetOtpInputGroup('sms');
  }

  private resetEmissionPins() {
    this.fEmission['newPin']?.reset('');
    this.fEmission['confirmPin']?.reset('');
    this.fEmission['otp']?.reset('');
    this.fEmission['newPin']?.markAsPristine();
    this.fEmission['confirmPin']?.markAsPristine();
    this.fEmission['otp']?.markAsPristine();
    this.fEmission['newPin']?.markAsUntouched();
    this.fEmission['confirmPin']?.markAsUntouched();
    this.fEmission['otp']?.markAsUntouched();

    // Pulisce anche il PIN del passo di autenticazione
    this.fContractSign?.['pin']?.reset('');
    this.fContractSign?.['pin']?.markAsPristine();
    this.fContractSign?.['pin']?.markAsUntouched();

    // Resetta i widget ng-ui-kit OTP/PIN correlati
    this.otpService.resetOtpInputGroup('contract-pin');
  }

  goToSuccessPage() {
    this.refreshSessionData();
    this.router.navigate(['/renew/success']);
  }

  // end emission form

  clearForms() {
    this.clearContractSignForm();
    this.clearRAOForm();
    this.clearCouponForm();
    this.clearPassphraseForm();
    this.clearEmissionForm();
  }

  public renewModalVisible = false;
  public renderRenewModalContent = false;

  openModal(name: string = '') {
    console.log('state: ', this.renewalState);

    this.renderRenewModalContent = true;

    if (name === 'COUPON') {
      this.paymentMode = 'COUPON';
    } else if (name === 'RAO') {
      this.paymentMode = 'RAO';
    } else if (name === 'SHOP') {
      if (this.onlyRAOpayment) {
        this.paymentMode = 'RAO';
      } else {
        this.paymentMode = 'SHOP';
      }

      //il rinnovo triss è solo via shop
    } else if (this.noPayment === false) {
      this.paymentMode = 'SHOP';
    }
    setTimeout(() => {
      this.renewModalVisible = true;
    }, 1100);
  }

  onToggleRenewModal(visible: boolean): void {
    this.closeModal();
  }

  closeModal() {
    this.clearForms();
    this.resetCheckboxes(); // se vuoi togliere le spunte

    // chiudi la modale
    this.renewModalVisible = false;
    this.renderRenewModalContent = false;

    // ricalcola quali step mostrare in base allo stato attuale
    this.checkRenewalState();
    console.log('state: ', this.renewalState);
  }

  resetCheckboxes() {
    //prima check
    const conditionsContainer = this.checkboxConditionsEl?.nativeElement;
    if (conditionsContainer) {
      const inputElConditions: HTMLInputElement | null =
        conditionsContainer.querySelector('input[type=checkbox]');
      if (inputElConditions && inputElConditions.checked) {
        inputElConditions.click(); // simula un clic per togliere la spunta
      }
    }

    //seconda check
    const confirmContainer = this.checkboxConfirmEl?.nativeElement;
    if (confirmContainer) {
      const inputElConfirm: HTMLInputElement | null =
        confirmContainer.querySelector('input[type=checkbox]');
      if (inputElConfirm && inputElConfirm.checked) {
        inputElConfirm.click(); // simula un clic per togliere la spunta
      }
    }

    //terza check
    const uuidNoticeContainer =
      this.checkboxUuidReplacementNoticeEl?.nativeElement;
    if (uuidNoticeContainer) {
      const inputElConfirmCheUuidReplacementNotice: HTMLInputElement | null =
        uuidNoticeContainer.querySelector('input[type=checkbox]');
      if (
        inputElConfirmCheUuidReplacementNotice &&
        inputElConfirmCheUuidReplacementNotice.checked
      ) {
        inputElConfirmCheUuidReplacementNotice.click(); // simula un clic per togliere la spunta
      }
    }
  }

  private async logoutAndCloseAllModals(): Promise<void> {
    sessionStorage.setItem('skipErrorModal', 'true');
    this.closeModal();

    try {
      await this.sessionService.logout();
    } finally {
      this.modalService.closeModal();
      this.bsModalService.hide();
      this.bsModalService.hide('error');
      setTimeout(() => {
        sessionStorage.removeItem('skipErrorModal');
      }, 2000);
    }
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  //refresho la session
  refreshSessionData() {
    this.sessionService.getUserSession().then(async session => {
      this.localStorageService.store('session', session);
      const certificates = await lastValueFrom(
        this.certificateService.getCertificateList()
      );
      this.certificateService.certID = certificates?.[0].certId;
      const certificate = await lastValueFrom(
        this.certificateService.getCertificate()
      );
      this.localStorageService.store('certificate', certificate);
      this.toasterService.clear();
    });
  }

  get renewalState(): string {
    return this.renewalData?.state || this.renewalData?.status || '';
  }

  private openAuthorizationIfNeeded() {
    if (this.isNewFlow) {
      //is.paymentMode = this.onlyRAOpayment ? 'RAO' : 'SHOP';
      this.showAuthorizationStep = true; // apre lo step 5
    }
  }

  private stopBusy() {
    this.loading = false;
    this.showSpinner = false;
  }

  /** migration vale solo per la firma remota. Sull'automatica non viene letto. */
  get followMigrationFlow(): boolean {
    if (this.automatic) {
      return false;
    }
    return !!this.renewalData?.migration;
  }

  private updateFlowFlags(): void {
    const migrationStatus = this.session?.migrationStatus;

    this.isNewFlow =
      this.IsTriss ||
      migrationStatus === 'AVAILABLE' ||
      this.isStellantis ||
      this.isMigration;

    this.isForcedLogout =
      (!this.IsTriss && migrationStatus === 'AVAILABLE') ||
      this.isStellantis ||
      this.isMigration;

    if (this.automatic) {
      this.isNewFlow = false;
      this.isForcedLogout = false;
    }

    if (this.isNewFlow) {
      this.contractSignStepNumber = 1;
      this.passphraseStepNumber = 2;
      this.emissionStepNumber = 3;
      this.authorizationStepNumber = 4;
    } else {
      this.contractSignStepNumber = 1;
      this.authorizationStepNumber = 2;
      this.passphraseStepNumber = 3;
      this.emissionStepNumber = 4;
    }

  }
}
